import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios'; // Axios usará la baseURL global de vite.config.js
import { Card, Spinner, Alert, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom'; // Importa useNavigate para la redirección
import '../../../assets/scss/bandeja-mensajes/QR.scss'; 


import { useAuth } from '../../../hooks/useAuth'; 

const QR = () => {
  const [qrUrl, setQrUrl] = useState(null);
  const [instanceId, setInstanceId] = useState(null); // ID de la instancia de bot (ej. 1 si es numérico)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [botStatus, setBotStatus] = useState('unknown');
  const [reloadMessage, setReloadMessage] = useState(null); // Estado del bot para el monitoreo

  const auth = useAuth(); 
  const currentAsesorId = auth.user ? auth.user.id : null; 
  const instanceIdFromAuth = auth.user ? auth.user.instanciaId : null; // Asume que useAuth provee 'instanciaId'

  const navigate = useNavigate(); 

  // Función para obtener la instancia_id del asesor, luego el QR y monitorear el estado
  const fetchQRAndMonitorStatus = useCallback(async () => {
    if (auth.isLoading) {
        setLoading(true); 
        return;
    }

    if (!auth.isAuthenticated || !currentAsesorId) {
      setError('No estás autenticado o ID de asesor no disponible. Por favor, inicia sesión.');
      setLoading(false);
      return;
    }

    setLoading(true); 
    setError(null);    
    setQrUrl(null);    
    setReloadMessage(null);

    try {
      let fetchedNumericInstanceId = instanceIdFromAuth; 

      if (!fetchedNumericInstanceId) {
        const asesorDetailsRes = await axios.get(`/asesor/me`); 
        
        if (asesorDetailsRes.data && typeof asesorDetailsRes.data.instanciaId === 'number') {
            fetchedNumericInstanceId = asesorDetailsRes.data.instanciaId;
        } else if (asesorDetailsRes.data && asesorDetailsRes.data.data && typeof asesorDetailsRes.data.data.instanciaId === 'number') {
            fetchedNumericInstanceId = asesorDetailsRes.data.data.instanciaId;
        } else {
            console.warn('Estructura de respuesta de /asesor/me inesperada o instanciaId no es un número.');
            setError('Error: No se pudo obtener el ID numérico de la instancia del bot.');
            setLoading(false);
            return;
        }
      }
        
      if (!fetchedNumericInstanceId) { 
        setError('Tu perfil de asesor no tiene una instancia de bot asignada o no se pudo obtener. Contacta a un administrador.');
        setLoading(false);
        return;
      }
      setInstanceId(fetchedNumericInstanceId); 

      const botInstanceNameForURL = `bot${fetchedNumericInstanceId}`; 

      const timestamp = new Date().getTime();

      const generatedQrUrl = `/api/instances/${botInstanceNameForURL}/qr?t=${timestamp}`;
      setQrUrl(generatedQrUrl);
      
      const statusRes = await axios.get(`/api/instances/${botInstanceNameForURL}?t=${timestamp}`); 
      
      let currentStatus = 'unknown';
      if (statusRes.data && statusRes.data.instance && typeof statusRes.data.instance.status === 'string') {
          currentStatus = statusRes.data.instance.status;
      } else {
          setBotStatus('estado desconocido'); 
          console.warn('No se pudo obtener el estado del bot desde /api/instances/:instanceId. Estructura de respuesta inesperada:', statusRes.data);
      }
      setBotStatus(currentStatus);

      setLoading(false); 

    } catch (err) {
      console.error('Error al cargar el QR o estado del bot:', err);
      if (axios.isAxiosError(err)) { 
        if (err.response) { 
          if (err.response.status === 404) {
            setError(`Error 404: El QR o la instancia (${fetchedNumericInstanceId ? `bot${fetchedNumericInstanceId}` : 'N/A'}) no están disponibles.`);
          } else if (err.response.status === 401) {
            setError('Sesión expirada. Por favor, inicia sesión nuevamente.');
            setTimeout(() => navigate('/login'), 2000); 
          } else if (err.response.data && err.response.data.message) {
            setError(`Error del servidor: ${err.response.data.message}`);
          } else {
            setError(`Error HTTP ${err.response.status}: ${err.response.statusText || 'Error desconocido del servidor.'}`);
          }
        } else if (err.request) { 
          setError('No se pudo conectar con el servidor. Verifica tu conexión de red o si el backend está activo.');
        } else { 
          setError(`Ocurrió un error: ${err.message}`);
        }
      } else {
        setError(`Ocurrió un error: ${err.message}`);
      }
      setLoading(false); 
      setReloadMessage(null);
    }
  }, [auth.isLoading, auth.isAuthenticated, currentAsesorId, instanceIdFromAuth, navigate]); 

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated && !error) { 
      fetchQRAndMonitorStatus(); 
    }

    let statusPollingInterval;
    if (instanceId && auth.isAuthenticated && !error) { 
      statusPollingInterval = setInterval(async () => {
        try {
          const botInstanceNameForPolling = `bot${instanceId}`; 
          const statusRes = await axios.get(`/api/instances/${botInstanceNameForPolling}`); 
          
          let currentStatus = 'unknown';
          if (statusRes.data && statusRes.data.instance && typeof statusRes.data.instance.status === 'string') {
              currentStatus = statusRes.data.instance.status;
          }
          setBotStatus(currentStatus); 

          if (currentStatus === 'connected') {
            console.log(`Bot ${instanceId} conectado. Redirigiendo a conversaciones...`);
            clearInterval(statusPollingInterval); 
            navigate('/bandeja-mensajes/conversaciones'); 
          }
        } catch (err) {
          console.error('Error al hacer polling de estado del bot:', err);
          clearInterval(statusPollingInterval); 
          setError('Conexión con el bot perdida o error al monitorear estado.');
          setBotStatus('desconectado/error'); 
        }
      }, 5000); 
    }

    return () => {
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval); 
      }
    };
  }, [fetchQRAndMonitorStatus, instanceId, auth.isAuthenticated, auth.isLoading, error, navigate]); 

  // Recarga automática del QR cada 8 s mientras no esté conectado
  useEffect(() => {
    if (instanceId && botStatus !== 'connected') {
      const qrImageInterval = setInterval(() => {
        const timestamp = new Date().getTime();
        setQrUrl(`/api/instances/bot${instanceId}/qr?t=${timestamp}`);
      }, 8000); // 8 segundos

      return () => clearInterval(qrImageInterval);
    }
  }, [instanceId, botStatus]);

  if (auth.isLoading) {
    return (
      <div className="qr-scan-panel-container d-flex flex-column align-items-center justify-content-center p-4">
        <Spinner animation="border" role="status" className="mb-3" />
        <p>Verificando autenticación...</p>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="qr-scan-panel-container d-flex flex-column align-items-center justify-content-center p-4">
        <Alert variant="danger">No estás autenticado. Por favor, <a href="/login">inicia sesión</a>.</Alert>
      </div>
    );
  }

  return (
    <div className="qr-scan-panel-container d-flex flex-column align-items-center justify-content-center p-4">
      <Card className="qr-code-box shadow-sm">
        <Card.Body className="text-center p-4">
          <h2 className="qr-title mb-4">
            <i className="bi bi-qr-code-scan me-2"></i> Escanea el Código QR para Conectar
          </h2>

          {loading && ( 
            <div className="qr-loader text-center my-4">
              <Spinner animation="border" role="status" className="mb-3" />
              <p>Cargando QR para tu bot...</p>
              <p className="text-muted">Asegúrate de que la instancia del bot esté activa y haya generado un QR.</p>
            </div>
          )}

          {error && <Alert variant="danger" className="my-4">{error}</Alert>}
          {reloadMessage && <Alert variant="info" className="my-3 w-100 text-center">{reloadMessage}</Alert>} {/* <--- MOSTRAR MENSAJE DE RECARGA */}

          {qrUrl && !loading && !error && (
            <div className="qr-content-wrapper">
              <img 
                src={qrUrl} 
                alt={`Código QR para bot: ${instanceId ? `bot${instanceId}` : 'tu bot'}`} 
                className="img-fluid border rounded shadow-sm mb-3 qr-code-image"
                onError={(e) => { 
                  e.target.onerror = null; 
                  setError('Error al cargar la imagen QR. El archivo podría no existir o ser inválido. Intenta recargar.'); 
                  setQrUrl(null); 
                }}
              />
              <p className="qr-instructions mb-3">
                1. Abre WhatsApp en tu teléfono.<br/>
                2. Toca Menú o Configuración.<br/>
                3. Selecciona "Dispositivos vinculados".<br/>
                4. Toca "Vincular un dispositivo".<br/>
                5. Escanea este código QR.
              </p>
              <p className="text-muted small">
                Bot ID: `{instanceId ? `bot${instanceId}` : 'Cargando...'}` | Estado: `{botStatus}`
              </p>
              <Button variant="outline-primary" onClick={fetchQRAndMonitorStatus}>
                <i className="bi bi-arrow-clockwise me-2"></i> Recargar QR
              </Button>
            </div>
          )}
           {!qrUrl && !loading && !error && (
            <Button variant="primary" onClick={fetchQRAndMonitorStatus}>
                Intentar Cargar QR
            </Button>
           )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default QR;